import { AlertCircle, CheckCircle2, RotateCw } from 'lucide-react';

import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { formatDateTime } from '@/lib/format';

interface NotificationMessageDetailsTabProps {
  notificationId: string;
  type: string;
  channel: string;
  audience: string;
  sentOn: string;
  sentBy: { name: string; role: string } | null;
  recipientsCount: number;
  deliveredCount: number;
  failedCount: number;
  pendingCount: number;
  message: string;
  charCount: number;
  smsParts: number;
  templateUsed: string | null;
  sentAt: string;
}

/** "Rajesh Kumar" → "RK", for the little round avatar beside the sender. */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '—';
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
  return `${first}${last}`.toUpperCase();
}

export function NotificationMessageDetailsTab({
  notificationId,
  type,
  channel,
  audience,
  sentOn,
  sentBy,
  recipientsCount,
  deliveredCount,
  failedCount,
  pendingCount,
  message,
  charCount,
  smsParts,
  templateUsed,
  sentAt,
}: NotificationMessageDetailsTabProps) {
  const senderName = sentBy ? `${sentBy.name} (${sentBy.role})` : 'An account since removed';

  /**
   * What actually happened, rather than the four fixed steps the design drew.
   *
   * This product sends synchronously and waits on the provider for each
   * message, so there is no queue to sit in and no separate "in progress" — the
   * honest timeline is composed, sent, and how it finished. A step the record
   * cannot support is left out rather than given an invented timestamp.
   *
   * Only two moments are recorded: when the send finished (`sentAt`), and when
   * each message was accepted — written as that same instant. Nothing records
   * when the message was written or when a failure came back, so those steps
   * carry no time rather than borrowing the send's.
   */
  const timeline: { title: string; at: string | null; detail: string; failed?: boolean }[] = [
    {
      title: 'Composed',
      at: null,
      detail: `Written by ${senderName}`,
    },
    {
      title: 'Sent',
      at: sentOn,
      detail: `${recipientsCount} ${recipientsCount === 1 ? 'message' : 'messages'} handed to the ${channel} provider`,
    },
  ];

  if (deliveredCount > 0) {
    timeline.push({
      title: 'Delivered',
      at: formatDateTime(sentAt),
      detail:
        failedCount === 0
          ? 'Every message was accepted for delivery'
          : `${deliveredCount} of ${recipientsCount} were accepted for delivery`,
    });
  }

  if (failedCount > 0) {
    timeline.push({
      title: 'Failed',
      at: null,
      detail: `${failedCount} could not be delivered. The reasons are on the delivery summary.`,
      failed: true,
    });
  }

  if (pendingCount > 0) {
    timeline.push({
      title: 'Still outstanding',
      at: null,
      detail: `${pendingCount} were accepted but had not arrived when the send finished`,
    });
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Top 2 Cards Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Card: Attributes Key-Value Details */}
        <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-6  shadow-xs flex flex-col gap-2.5 text-sm">
          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Notification ID</span>
            <span
              className="text-sm font-black text-slate-700 dark:text-slate-100 font-mono truncate max-w-[190px]"
              title={notificationId}
            >
              {notificationId}
            </span>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Notification type</span>
            <span className="text-sm font-extrabold text-slate-700 dark:text-slate-100 truncate max-w-[190px]" title={type}>
              {type}
            </span>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Channel</span>
            <Badge tone={channel === 'SMS' ? 'accent' : 'success'}>
              {channel}
            </Badge>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Audience</span>
            <span className="text-sm font-extrabold text-slate-700 dark:text-slate-100 truncate max-w-[190px]" title={audience}>
              {audience}
            </span>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Sent on</span>
            <span className="text-sm font-bold text-slate-700 dark:text-slate-100">{sentOn}</span>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Sent by</span>
            <div className="flex items-center gap-2">
              <span className="h-6 w-6 rounded-full bg-indigo-600 dark:bg-indigo-500 text-white font-extrabold text-[10px] flex items-center justify-center">
                {sentBy ? initialsOf(sentBy.name) : '—'}
              </span>
              <span className="text-sm font-extrabold text-slate-700 dark:text-slate-100">{senderName}</span>
            </div>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Recipients</span>
            <span className="text-sm font-black text-slate-700 dark:text-white">{recipientsCount}</span>
          </div>

          <div className="flex flex-col gap-1.5 pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Message</span>
            <span className="text-[13.5px] font-bold text-slate-700 dark:text-slate-300 leading-relaxed">{message}</span>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Character count</span>
            <span className="text-sm font-black text-slate-700 dark:text-slate-100">{charCount}</span>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">SMS parts</span>
            <span className="text-sm font-black text-slate-700 dark:text-slate-100">{smsParts}</span>
          </div>

          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Template used</span>
            <span className="text-sm font-extrabold text-slate-700 dark:text-slate-100 truncate max-w-[190px]">
              {templateUsed ?? 'None — written from scratch'}
            </span>
          </div>

          {/* This product records no reference against a send and cannot carry
              an attachment on either channel, so both read as "not recorded"
              rather than being given something invented. */}
          <div className="flex items-center justify-between pb-1 border-b border-slate-100 dark:border-slate-800/80">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Reference</span>
            <span className="text-sm font-bold text-slate-400">—</span>
          </div>

          <div className="flex items-center justify-between pb-1">
            <span className="text-sm font-bold text-slate-500 dark:text-slate-400">Attachments</span>
            <span className="text-sm font-bold text-slate-400">—</span>
          </div>
        </div>

        {/* Right Card: Delivery Timeline */}
        <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 p-6  shadow-xs flex flex-col gap-5">
          <h3 className="text-xl font-extrabold text-slate-700 dark:text-white tracking-tight">
            Delivery timeline
          </h3>

          <div className="flex flex-col gap-7 relative pl-3">
            {timeline.map((step, index) => (
              <div key={step.title} className="flex items-start gap-4 relative">
                {index < timeline.length - 1 && (
                  <div
                    className={`absolute left-[13px] top-7 bottom-[-28px] w-[2px] ${
                      step.failed
                        ? 'bg-rose-500/40 dark:bg-rose-500/30'
                        : 'bg-emerald-500/40 dark:bg-emerald-500/30'
                    }`}
                  />
                )}
                <div
                  className={`h-7 w-7 rounded-full flex items-center justify-center z-10 shrink-0 border-2 ${
                    step.failed
                      ? 'bg-rose-50 dark:bg-rose-950/60 border-rose-500 text-rose-600 dark:text-rose-400'
                      : 'bg-emerald-50 dark:bg-emerald-950/60 border-emerald-500 text-emerald-600 dark:text-emerald-400'
                  }`}
                >
                  {step.failed ? (
                    <AlertCircle className="h-4 w-4" />
                  ) : (
                    <CheckCircle2 className="h-4 w-4" />
                  )}
                </div>
                <div className="flex flex-col">
                  <span className="text-[14px] font-black text-slate-700 dark:text-white leading-tight">
                    {step.title}
                  </span>
                  {step.at && (
                    <span className="text-sm text-slate-500 dark:text-slate-400 font-bold font-mono mt-0.5">
                      {step.at}
                    </span>
                  )}
                  <span className="text-sm font-semibold text-slate-600 dark:text-slate-300 mt-0.5">
                    {step.detail}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Card: Preview & Actions */}
      <div className="rounded-3xl bg-white dark:bg-[#111c2e] border border-slate-200 dark:border-slate-800 px-6 py-4 shadow-xs flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div className="flex flex-col gap-3.5 max-w-md w-full">
          <h3 className="text-xl font-extrabold text-slate-700 dark:text-white tracking-tight">Preview</h3>
          {/* Phone Message Container */}
          <div className="rounded-2xl bg-slate-100 dark:bg-[#0b1322] border border-slate-200 dark:border-slate-800 p-4 flex flex-col gap-2">
            <div className="bg-white dark:bg-[#111c2e] text-slate-700 dark:text-slate-100 border border-slate-200 dark:border-slate-700/80 rounded-2xl rounded-tl-sm p-4 text-[13.5px] font-bold leading-relaxed shadow-xs">
              <p>{message}</p>
              <span className="text-[11px] text-slate-400 block text-right mt-2 font-mono font-bold">
                {sentOn}
              </span>
            </div>
          </div>
        </div>

        {/* Resending is a second send that costs money and arrives on real
            handsets. There is no endpoint for it, and a button that only
            claimed to have done it would be the most expensive lie on this
            page — so it says plainly that it is not available yet. */}
        <Button
          variant="secondary"
          size="sm"
          disabled
          title="Resending to the failed recipients is not available yet"
          className="text-[14px] font-extrabold border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0b1322] text-slate-800 dark:text-slate-100 hover:bg-slate-50 dark:hover:bg-slate-800 shadow-xs cursor-pointer py-2.5 px-4 rounded-xl flex items-center gap-2 transition-all self-start sm:self-end"
        >
          <RotateCw className="h-4 w-4" />
          <span>Resend to failed</span>
        </Button>
      </div>
    </div>
  );
}
